import { useIonViewWillEnter } from '@ionic/react';
import {
  useQuery,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import {
  ClaimRequestStatus,
  ProductsApi,
  Permission,
} from '@up2stay/up2promo-api-client';
import {
  buildBehaviourLabelMap,
  buildProductsListSearch,
  EMPTY_PRODUCTS_LIST_FILTERS,
  parseProductsLocationIdFromSearch,
  sortProductsForList,
  UNKNOWN_FACET_VALUE,
  type ProductsListFilterValues,
  type ProductsListSortOption,
} from '@up2stay/up2promo-commons/products';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';

import {
  fillClaimTemplate,
  useClaimRequests,
} from '@/core/hooks/use-claim-requests';
import { useLocations } from '@/core/hooks/use-locations';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { usePermissions } from '@/core/hooks/use-permissions';
import { useWorkspaceMembersRoster } from '@/core/hooks/use-workspace-members-roster';
import { useWorkspaceRealtime } from '@/core/hooks/use-workspace-realtime';
import { buildProductsSetupPath } from '@/core/lib/build-products-setup-path';
import { parseSetupTypeParam } from '@/core/lib/product-setup-type';
import { useDictionary, useLocale } from '@/core/providers/dictionary-provider';
import { claimRequestsQueryKeys } from '@/core/query-keys/claim-requests-query-keys';
import { productsQueryKeys } from '@/core/query-keys/products-query-keys';
import { canonicalizeShellPathname, shellPaths } from '@/core/router/app-paths';
import { useCurrentWorkspace } from '@/core/store/workspace.store';
import {
  type ClaimRequestUpdatedRealtimeEvent,
  type RealtimeEvent,
  toProductScan,
} from '@/core/types/realtime-events';

import {
  PRODUCTS_PAGE_SIZE_DEFAULT,
  type ProductsDict,
} from '@/routes/app/products';

export function useProductsPage() {
  const dict = useDictionary();
  const locale = useLocale();
  const { navigateShell } = useNavigateShell();
  const location = useLocation();
  const currentWorkspace = useCurrentWorkspace();
  const { can } = usePermissions();
  const canManageProducts = can(Permission.PRODUCTS_MANAGE);
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(PRODUCTS_PAGE_SIZE_DEFAULT);
  const [productFilters, setProductFilters] =
    useState<ProductsListFilterValues>(() => ({
      ...EMPTY_PRODUCTS_LIST_FILTERS,
      locationId: parseProductsLocationIdFromSearch(location.search),
    }));
  const [listSort, setListSort] = useState<ProductsListSortOption>('default');
  const [liveScanEnabled, setLiveScanEnabled] = useState(false);
  const [highlightedProductId, setHighlightedProductId] = useState<
    string | null
  >(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const seenScanKeysRef = useRef(new Set<string>());
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const applyLocationFilterFromSearch = useCallback((search: string) => {
    const locationId = parseProductsLocationIdFromSearch(search);
    let changed = false;
    setProductFilters((prev) => {
      if (prev.locationId === locationId) return prev;
      changed = true;
      return { ...prev, locationId };
    });
    if (changed) setPage(1);
  }, []);

  const claimRequests = useClaimRequests();

  useIonViewWillEnter(() => {
    void queryClient.invalidateQueries({
      queryKey: productsQueryKeys.root,
    });
    void queryClient.invalidateQueries({
      queryKey: claimRequestsQueryKeys.root,
    });
    void queryClient.invalidateQueries({
      queryKey: productsQueryKeys.facetsRoot,
    });
    void queryClient.invalidateQueries({
      queryKey: ['workspace-products-metrics'],
    });
    // Ionic keeps this page in the tab stack — re-apply ?locationId= on enter.
    applyLocationFilterFromSearch(location.search);
  });

  useEffect(() => {
    // Legacy `/products?hash=` links only. Ionic keeps this page mounted under
    // setup, where the router location is `/products/setup?hash=` — re-routing
    // from here would root-replace the tab stack (DEV-469).
    if (
      canonicalizeShellPathname(location.pathname) !== shellPaths.moreProducts
    ) {
      return;
    }
    const hash = new URLSearchParams(location.search).get('hash')?.trim();
    if (!hash) return;
    const params = new URLSearchParams(location.search);
    params.delete('hash');
    const productType = parseSetupTypeParam(
      params.get('t'),
      params.get('type'),
    );
    params.delete('t');
    params.delete('type');
    const rest = params.toString();
    const setupPath = buildProductsSetupPath({
      hash,
      productType,
    });
    navigateShell(rest ? `${setupPath}&${rest}` : setupPath);
  }, [location.pathname, location.search, navigateShell]);

  // Sync location filter when ?locationId= changes (location detail CTAs).
  // Needed because Ionic keeps this page alive — initial useState alone misses
  // navigations that only update the URL.
  useEffect(() => {
    const search = location.search;
    queueMicrotask(() => {
      applyLocationFilterFromSearch(search);
    });
  }, [location.search, applyLocationFilterFromSearch]);

  const productsDict = (dict as { products?: ProductsDict }).products;

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 300);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchQuery]);

  const syncLocationIdToUrl = useCallback(
    (locationId: string | null) => {
      const current = parseProductsLocationIdFromSearch(location.search);
      if (current === locationId) return;
      const query = buildProductsListSearch(location.search, locationId);
      navigateShell(
        query ? `${shellPaths.moreProducts}?${query}` : shellPaths.moreProducts,
        { intent: 'updateQuery' },
      );
    },
    [location.search, navigateShell],
  );

  const handleFiltersChange = useCallback(
    (next: ProductsListFilterValues) => {
      setProductFilters(next);
      setPage(1);
      syncLocationIdToUrl(next.locationId);
    },
    [syncLocationIdToUrl],
  );

  const { locations } = useLocations();
  const locationIdToName = useMemo(() => {
    const map: Record<string, string> = {};
    for (const loc of locations) {
      if (loc.id) map[loc.id] = loc.name ?? loc.id;
    }
    return map;
  }, [locations]);

  const workspaceId = currentWorkspace?.id;
  const { data: workspaceMembers = [] } = useWorkspaceMembersRoster(
    workspaceId,
    'products-list',
  );
  const userIdToMember = useMemo(() => {
    const map: Record<string, (typeof workspaceMembers)[number]> = {};
    for (const member of workspaceMembers) {
      const userId = member.userId?.trim();
      if (userId) map[userId] = member;
    }
    return map;
  }, [workspaceMembers]);

  const locationFilterOptions = useMemo(() => {
    const collatorLocale = locale === 'de' ? 'de' : 'en';
    return [...locations]
      .filter((loc) => loc.id)
      .map((loc) => ({
        id: loc.id as string,
        label: loc.name ?? loc.id ?? '',
      }))
      .sort((a, b) =>
        a.label.localeCompare(b.label, collatorLocale, { sensitivity: 'base' }),
      );
  }, [locations, locale]);

  const behaviorsKey = useMemo(
    () => [...productFilters.behaviors].sort().join(','),
    [productFilters.behaviors],
  );
  const formsKey = useMemo(
    () => [...productFilters.forms].sort().join(','),
    [productFilters.forms],
  );
  const materialsKey = useMemo(
    () => [...productFilters.materials].sort().join(','),
    [productFilters.materials],
  );
  const colorsKey = useMemo(
    () => [...productFilters.colors].sort().join(','),
    [productFilters.colors],
  );
  const sizesKey = useMemo(
    () => [...productFilters.sizes].sort().join(','),
    [productFilters.sizes],
  );
  const languagesKey = useMemo(
    () => [...productFilters.languages].sort().join(','),
    [productFilters.languages],
  );

  const listFilterQuery = useMemo(
    () => ({
      knownOnly: true as const,
      ...(debouncedSearch.trim() ? { filter: debouncedSearch.trim() } : {}),
      ...(productFilters.locationId
        ? { locationId: productFilters.locationId }
        : {}),
      ...(productFilters.behaviors.length > 0
        ? { behaviors: productFilters.behaviors }
        : {}),
      ...(productFilters.forms.length > 0
        ? { forms: productFilters.forms }
        : {}),
      ...(productFilters.materials.length > 0
        ? { materials: productFilters.materials }
        : {}),
      ...(productFilters.colors.length > 0
        ? { colors: productFilters.colors }
        : {}),
      ...(productFilters.sizes.length > 0
        ? { sizes: productFilters.sizes }
        : {}),
      ...(productFilters.languages.length > 0
        ? { languages: productFilters.languages }
        : {}),
    }),
    [
      debouncedSearch,
      productFilters.locationId,
      productFilters.behaviors,
      productFilters.forms,
      productFilters.materials,
      productFilters.colors,
      productFilters.sizes,
      productFilters.languages,
    ],
  );

  const { data: facetsData } = useQuery({
    queryKey: productsQueryKeys.facets(
      currentWorkspace?.id,
      debouncedSearch,
      behaviorsKey,
      formsKey,
      materialsKey,
      colorsKey,
      sizesKey,
      languagesKey,
      productFilters.locationId,
    ),
    queryFn: async () => {
      if (!currentWorkspace) return undefined;
      const res = await ProductsApi.findFacets({
        path: { workspaceId: currentWorkspace.id },
        query: listFilterQuery,
      });
      return res.data;
    },
    enabled: !!currentWorkspace?.id,
    staleTime: 30 * 1000,
  });

  const {
    data: productsData,
    isLoading: productsLoading,
    isFetching: productsFetching,
    isError: productsError,
    refetch: refetchProducts,
  } = useQuery({
    queryKey: productsQueryKeys.list(
      currentWorkspace?.id,
      debouncedSearch,
      page,
      pageSize,
      behaviorsKey,
      formsKey,
      materialsKey,
      colorsKey,
      sizesKey,
      languagesKey,
      productFilters.locationId,
    ),
    queryFn: async () => {
      if (!currentWorkspace) {
        return {
          data: [],
          totalPages: 1,
          hasNextPage: false,
          totalCount: 0,
        };
      }
      const res = await ProductsApi.findAll({
        path: { workspaceId: currentWorkspace.id },
        query: {
          page,
          limit: pageSize,
          includeScansDays: 30,
          includeScanSeriesDays: 30,
          ...listFilterQuery,
        },
      });
      return {
        data: res.data?.data ?? [],
        totalPages: res.data?.totalPages ?? 1,
        hasNextPage: res.data?.hasNextPage ?? false,
        totalCount: res.data?.totalCount ?? 0,
      };
    },
    enabled: !!currentWorkspace?.id,
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
  });

  const { data: metricsData, isLoading: metricsLoading } = useQuery({
    queryKey: ['workspace-products-metrics', currentWorkspace?.id],
    queryFn: async () => {
      if (!currentWorkspace) {
        return { totalProducts: 0, totalScans30d: 0 };
      }

      const pageLimit = 100;
      const maxPages = 20;
      const first = await ProductsApi.findAll({
        path: { workspaceId: currentWorkspace.id },
        query: {
          page: 1,
          limit: pageLimit,
          includeScansDays: 30,
          knownOnly: true,
        },
      });
      const totalProducts = first.data?.totalCount ?? 0;
      let totalScans30d = (first.data?.data ?? []).reduce(
        (sum, item) => sum + (item.scanCount ?? 0),
        0,
      );
      const totalPages = Math.min(first.data?.totalPages ?? 1, maxPages);
      for (let metricsPage = 2; metricsPage <= totalPages; metricsPage++) {
        const pageRes = await ProductsApi.findAll({
          path: { workspaceId: currentWorkspace.id },
          query: {
            page: metricsPage,
            limit: pageLimit,
            includeScansDays: 30,
            knownOnly: true,
          },
        });
        totalScans30d += (pageRes.data?.data ?? []).reduce(
          (sum, item) => sum + (item.scanCount ?? 0),
          0,
        );
      }

      return { totalProducts, totalScans30d };
    },
    enabled: !!currentWorkspace?.id,
    staleTime: 30 * 1000,
  });

  const products = useMemo(
    () => productsData?.data ?? [],
    [productsData?.data],
  );
  const totalPages = productsData?.totalPages ?? 1;
  const totalCount = productsData?.totalCount ?? 0;
  const totalProducts = metricsData?.totalProducts ?? 0;
  const showMetrics = totalProducts > 0 || products.length > 0;

  const sortedProducts = useMemo(() => {
    const collatorLocale = locale === 'de' ? 'de' : 'en';
    return sortProductsForList(
      products,
      listSort,
      collatorLocale,
      locationIdToName,
    );
  }, [products, listSort, locale, locationIdToName]);

  const hasActiveFilters =
    productFilters.behaviors.length > 0 ||
    productFilters.forms.length > 0 ||
    productFilters.materials.length > 0 ||
    productFilters.colors.length > 0 ||
    productFilters.sizes.length > 0 ||
    productFilters.languages.length > 0 ||
    !!productFilters.locationId;
  const hasActiveSearchOrFilters = !!debouncedSearch.trim() || hasActiveFilters;

  const filterLabels = useMemo(() => {
    const edit = productsDict?.edit ?? {};
    const identity = productsDict?.identity;
    const listFilters = productsDict?.list?.filters;
    return {
      filter: productsDict?.list?.filter ?? 'Filter',
      filtersTitle: listFilters?.title ?? 'Filter products',
      clearAll: listFilters?.clearAll ?? 'Clear all',
      done: listFilters?.done ?? 'Done',
      sectionBehaviour: listFilters?.sectionBehaviour ?? 'Redirect',
      sectionForm: listFilters?.sectionForm ?? 'Form',
      sectionMaterial: listFilters?.sectionMaterial ?? 'Material',
      sectionColor: listFilters?.sectionColor ?? 'Color',
      sectionSize: listFilters?.sectionSize ?? 'Size',
      sectionLocale: listFilters?.sectionLocale ?? 'Locale',
      sectionLocation: listFilters?.sectionLocation ?? 'Location',
      allLocations: listFilters?.allLocations ?? 'All locations',
      noLocations: listFilters?.noLocations ?? 'No locations yet.',
      unknown: listFilters?.unknown ?? 'Unknown',
      behaviourLabels: buildBehaviourLabelMap(edit),
      formLabels: {
        STAND: identity?.formStand ?? 'Stand',
        CARD: identity?.formCard ?? 'Business card',
        BADGE: identity?.formBadge ?? 'Badge',
      },
      materialLabels: {
        PVC: identity?.materialPvc ?? 'PVC',
        METAL: identity?.materialMetal ?? 'Metal',
        ACRYL: identity?.materialAcryl ?? 'Acrylic',
        GENERIC: identity?.materialGeneric ?? 'Generic',
      },
    };
  }, [productsDict]);

  const sortLabels = useMemo(
    () => ({
      sort: productsDict?.list?.sort ?? 'Sort',
      options: {
        default: productsDict?.list?.sortOptions?.default ?? 'Default',
        nameAsc: productsDict?.list?.sortOptions?.nameAsc ?? 'Name',
        scansDesc: productsDict?.list?.sortOptions?.scansDesc ?? 'Most scanned',
        locationAsc: productsDict?.list?.sortOptions?.locationAsc ?? 'Location',
      },
    }),
    [productsDict?.list?.sort, productsDict?.list?.sortOptions],
  );

  const activeFilterChips = useMemo(() => {
    const chips: Array<{ key: string; label: string; onRemove: () => void }> =
      [];
    for (const behaviour of productFilters.behaviors) {
      const label =
        behaviour === UNKNOWN_FACET_VALUE
          ? filterLabels.unknown
          : (filterLabels.behaviourLabels[behaviour] ?? behaviour);
      chips.push({
        key: `behaviour-${behaviour}`,
        label,
        onRemove: () =>
          handleFiltersChange({
            ...productFilters,
            behaviors: productFilters.behaviors.filter((t) => t !== behaviour),
          }),
      });
    }
    for (const form of productFilters.forms) {
      chips.push({
        key: `form-${form}`,
        label:
          form === UNKNOWN_FACET_VALUE
            ? filterLabels.unknown
            : (filterLabels.formLabels[
                form as keyof typeof filterLabels.formLabels
              ] ?? form),
        onRemove: () =>
          handleFiltersChange({
            ...productFilters,
            forms: productFilters.forms.filter((f) => f !== form),
          }),
      });
    }
    for (const material of productFilters.materials) {
      chips.push({
        key: `material-${material}`,
        label:
          material === UNKNOWN_FACET_VALUE
            ? filterLabels.unknown
            : (filterLabels.materialLabels[
                material as keyof typeof filterLabels.materialLabels
              ] ?? material),
        onRemove: () =>
          handleFiltersChange({
            ...productFilters,
            materials: productFilters.materials.filter((m) => m !== material),
          }),
      });
    }
    for (const color of productFilters.colors) {
      chips.push({
        key: `color-${color}`,
        label: color === UNKNOWN_FACET_VALUE ? filterLabels.unknown : color,
        onRemove: () =>
          handleFiltersChange({
            ...productFilters,
            colors: productFilters.colors.filter((c) => c !== color),
          }),
      });
    }
    for (const size of productFilters.sizes) {
      chips.push({
        key: `size-${size}`,
        label: size === UNKNOWN_FACET_VALUE ? filterLabels.unknown : size,
        onRemove: () =>
          handleFiltersChange({
            ...productFilters,
            sizes: productFilters.sizes.filter((s) => s !== size),
          }),
      });
    }
    for (const locale of productFilters.languages) {
      chips.push({
        key: `locale-${locale}`,
        label: locale === UNKNOWN_FACET_VALUE ? filterLabels.unknown : locale,
        onRemove: () =>
          handleFiltersChange({
            ...productFilters,
            languages: productFilters.languages.filter((l) => l !== locale),
          }),
      });
    }
    if (productFilters.locationId) {
      chips.push({
        key: `loc-${productFilters.locationId}`,
        label:
          locationIdToName[productFilters.locationId] ??
          productFilters.locationId,
        onRemove: () =>
          handleFiltersChange({ ...productFilters, locationId: null }),
      });
    }
    return chips;
  }, [
    productFilters,
    filterLabels.unknown,
    filterLabels.behaviourLabels,
    filterLabels.formLabels,
    filterLabels.materialLabels,
    locationIdToName,
    handleFiltersChange,
  ]);

  const paginationRange = useMemo(() => {
    if (totalCount === 0) {
      return { from: 0, to: 0 };
    }
    const from = (page - 1) * pageSize + 1;
    const to = Math.min(page * pageSize, totalCount);
    return { from, to };
  }, [page, pageSize, totalCount]);

  const showingLabel = useMemo(() => {
    const template =
      productsDict?.pagination?.showing ?? 'Showing {from}–{to} of {total}';
    return template
      .replace('{from}', String(paginationRange.from))
      .replace('{to}', String(paginationRange.to))
      .replace('{total}', String(totalCount));
  }, [productsDict?.pagination?.showing, paginationRange, totalCount]);

  const handleProductClick = useCallback(
    (product: { id?: string }) => {
      if (product.id) {
        navigateShell(shellPaths.moreProductDetail(product.id));
      }
    },
    [navigateShell],
  );

  const liveScanLabels = useMemo(
    () => ({
      toggle: productsDict?.liveScan?.toggle ?? 'Live scan',
      hint:
        productsDict?.liveScan?.hint ??
        'Highlight products when someone scans their code in real time.',
      statusConnecting:
        productsDict?.liveScan?.statusConnecting ?? 'Connecting…',
      statusLive: productsDict?.liveScan?.statusLive ?? 'Live',
      statusError: productsDict?.liveScan?.statusError ?? 'Disconnected',
      statusOff: productsDict?.liveScan?.statusOff ?? 'Off',
    }),
    [productsDict?.liveScan],
  );

  const highlightProduct = useCallback((productId: string) => {
    setHighlightedProductId(productId);
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }
    highlightTimeoutRef.current = setTimeout(() => {
      setHighlightedProductId(null);
    }, 5000);
    requestAnimationFrame(() => {
      document
        .querySelector(`[data-product-id="${productId}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }, []);

  const claimDict = dict.claim.pending;

  /**
   * A claim request changed (DEV-782): refresh the drawer; a verified claim
   * puts the product into the list, a rejection says why (DEV-756).
   */
  const handleClaimUpdated = useCallback(
    (event: ClaimRequestUpdatedRealtimeEvent) => {
      void queryClient.invalidateQueries({
        queryKey: claimRequestsQueryKeys.list(currentWorkspace?.id),
      });
      if (event.status === ClaimRequestStatus.VERIFIED) {
        void queryClient
          .invalidateQueries({
            queryKey: productsQueryKeys.all(currentWorkspace?.id),
          })
          .then(() => highlightProduct(event.productId));
        void queryClient.invalidateQueries({
          queryKey: ['workspace-products-metrics', currentWorkspace?.id],
        });
        toast.success(
          fillClaimTemplate(claimDict.toastClaimed, { hash: event.hash }),
          {
            action: {
              label: claimDict.toastView,
              onClick: () =>
                navigateShell(shellPaths.moreProductDetail(event.productId)),
            },
            duration: 8000,
          },
        );
        return;
      }
      if (event.status === ClaimRequestStatus.REJECTED) {
        toast.error(
          fillClaimTemplate(claimDict.toastRejected, { hash: event.hash }),
          {
            description: event.rejectionReason
              ? fillClaimTemplate(claimDict.rejectedReason, {
                  reason: event.rejectionReason,
                })
              : undefined,
            duration: 10000,
          },
        );
      }
    },
    [
      currentWorkspace?.id,
      claimDict,
      highlightProduct,
      navigateShell,
      queryClient,
    ],
  );

  const handleRealtimeEvent = useCallback(
    (event: RealtimeEvent) => {
      if (event.type === 'claim_request.updated') {
        handleClaimUpdated(event);
        return;
      }
      if (!liveScanEnabled) return;
      const scan = toProductScan(event);
      if (!scan) return;

      const dedupeKey = `${scan.productId}:${scan.scannedAt}`;
      if (seenScanKeysRef.current.has(dedupeKey)) return;
      seenScanKeysRef.current.add(dedupeKey);
      if (seenScanKeysRef.current.size > 100) {
        const oldest = seenScanKeysRef.current.values().next().value;
        if (oldest) seenScanKeysRef.current.delete(oldest);
      }

      void queryClient.invalidateQueries({
        queryKey: ['workspace-products-metrics', currentWorkspace?.id],
      });
      void queryClient.invalidateQueries({
        queryKey: productsQueryKeys.all(currentWorkspace?.id),
      });

      const productInList = products.find((p) => p.id === scan.productId);
      const displayName = scan.label?.trim() || scan.hash || scan.productId;

      if (productInList?.id) {
        highlightProduct(productInList.id);
        return;
      }

      const toastTemplate =
        productsDict?.liveScan?.toastScanned ?? '{name} was just scanned';
      toast.info(toastTemplate.replace('{name}', displayName), {
        action: {
          label: productsDict?.liveScan?.toastView ?? 'View',
          onClick: () =>
            navigateShell(shellPaths.moreProductDetail(scan.productId)),
        },
        duration: 8000,
      });
    },
    [
      currentWorkspace?.id,
      handleClaimUpdated,
      highlightProduct,
      liveScanEnabled,
      products,
      productsDict?.liveScan,
      navigateShell,
      queryClient,
    ],
  );

  // Pending claims keep the stream open so a decision shows up live.
  const claimsLive = claimRequests.pending.length > 0;
  const { connectionState } = useWorkspaceRealtime({
    workspaceId: currentWorkspace?.id,
    enabled: liveScanEnabled || claimsLive,
    onEvent: handleRealtimeEvent,
  });

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  return {
    dict,
    locale,
    navigateShell,
    location,
    currentWorkspace,
    canManageProducts,
    queryClient,
    searchQuery,
    setSearchQuery,
    debouncedSearch,
    page,
    setPage,
    pageSize,
    setPageSize,
    productFilters,
    setProductFilters,
    listSort,
    setListSort,
    liveScanEnabled,
    setLiveScanEnabled,
    highlightedProductId,
    productsDict,
    handleFiltersChange,
    locations,
    locationIdToName,
    userIdToMember,
    locationFilterOptions,
    facetsData,
    productsData,
    productsLoading,
    productsFetching,
    productsError,
    refetchProducts,
    metricsData,
    metricsLoading,
    products,
    totalPages,
    totalCount,
    totalProducts,
    showMetrics,
    sortedProducts,
    hasActiveFilters,
    hasActiveSearchOrFilters,
    filterLabels,
    sortLabels,
    activeFilterChips,
    paginationRange,
    showingLabel,
    handleProductClick,
    liveScanLabels,
    connectionState,
    claimRequests,
    claimsLive: claimsLive && connectionState === 'open',
    EMPTY_PRODUCTS_LIST_FILTERS,
  };
}

export type UseProductsPageReturn = ReturnType<typeof useProductsPage>;
